import React from 'react';

import { Button } from '@/components/ui/button';
// the store module only: the flow barrel carries the sign-out dialog.
import { selectBothFlowsUnlocked, useFlowStore } from '@/features/flow/store';
import { cn } from '@/lib/utils';

import { MIGRATION_COPY } from '../copy';
import { useMigrationStore } from '../store';
import { useMigrateQuote } from '../useMigrateQuote';
import type { MigrateQuoteState } from '../useMigrateQuote';

interface MigrateButtonProps {
    compact?: boolean;
    className?: string;
}

/**
 * Something can actually be migrated: the account has a V1 wallet and the
 * migrate quote (`GET /speed/migrate/quote`) is above zero. Not the
 * withdrawable balance: that one applies the withdrawal floor and the
 * either/or bucket rule, so 2 USDC on HyperCore reads 0 there while 0.99 USDC
 * above the 1.01 reserve can move. A quote not read yet hides the button.
 */
export function hasSomethingToMigrate(
    quote: Pick<MigrateQuoteState, 'hasWallet' | 'migratable'>,
): boolean {
    return quote.hasWallet && quote.migratable !== null && quote.migratable > 0;
}

/** Reads the migrate quote; mounted only while V1 is in use and both flows are unlocked. */
const MigrateButtonWhenFunded: React.FC<MigrateButtonProps> = ({
    compact = false,
    className,
}) => {
    const quote = useMigrateQuote();
    const open = useMigrationStore((state) => state.open);

    if (!hasSomethingToMigrate(quote)) return null;

    return (
        <Button
            type="button"
            size={compact ? 'sm' : 'default'}
            title={MIGRATION_COPY.buttonTitle}
            onClick={open}
            className={cn(
                'bg-sky-500 px-2.5 text-xs font-semibold text-black hover:bg-sky-400',
                className,
            )}
        >
            {MIGRATION_COPY.buttonLabel}
        </Button>
    );
};

/**
 * The header's Migrate button, right after the V1/V2 toggle: shown while V1
 * is the flow in use, both flows are unlocked for the account and something
 * can be migrated (see {@link hasSomethingToMigrate}). Opens the migration
 * dialog.
 *
 * The quote is read only behind that gate, so V2 (and an account with one
 * flow) never asks MoonHub.API for it.
 */
const MigrateButton: React.FC<MigrateButtonProps> = (props) => {
    const isV1 = useFlowStore((state) => state.active === 'v1');
    const bothUnlocked = useFlowStore(selectBothFlowsUnlocked);
    if (!isV1 || !bothUnlocked) return null;
    return <MigrateButtonWhenFunded {...props} />;
};

MigrateButton.displayName = 'MigrateButton';

export default MigrateButton;
